/**
 * Retrieves a nested value from an object using a dot-separated path.
 *
 * @param {Object} obj - The object to extract the value from.
 * @param {string} path - The path to the nested value (e.g., "a.b.c").
 * @returns {*} - The value at the given path, or `undefined` if it doesn't exist.
 */
const getNestedValue = (obj, path) => path.split('.').reduce((acc, part) => acc && acc[part], obj);

/**
 * Sorts an array of objects based on the order of keys in a string array,
 * matching the corresponding values in a nested key path.
 *
 * @param {string[]} strings - Array of string keys to sort by.
 * @param {Object[]} objects - Array of objects to sort.
 * @param {string} keyPath - Dot-separated path to the nested value in each object.
 * @param {Object} objStrValues - Mapping of string keys to actual
 * string values to match against object values.
 * @returns {Object[]} - New array with matched objects sorted by string order,
 * followed by unmatched objects.
 */
const sortObjectsByStringOrder = (strings, objects, keyPath, objStrValues) => {
  // Validate inputs
  if (
    !Array.isArray(strings)
    || !Array.isArray(objects)
    || typeof keyPath !== 'string'
    || typeof objStrValues !== 'object'
    || objStrValues === null
  ) {
    return objects || [];
  }

  if (strings.length === 0) return objects;

  const matched = [];
  const matchedSet = new Set();

  strings.forEach((str) => {
    objects.forEach((obj, index) => {
      const value = getNestedValue(obj, keyPath);
      if (!matchedSet.has(index) && objStrValues[str] === value) {
        matched.push(obj);
        matchedSet.add(index);
      }
    });
  });

  const unmatched = objects.filter((_, index) => !matchedSet.has(index));
  return [...matched, ...unmatched];
};

export default sortObjectsByStringOrder;
